import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pms_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Only force-redirect on true session failures. Domain errors (e.g. wrong
// approval password) also return 401 and must NOT kick the user to /login.
const SESSION_FAIL_DETAILS = new Set([
  "Not authenticated",
  "Token expired",
  "Invalid token",
  "User not found",
]);

api.interceptors.response.use(
  (r) => r,
  (e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 401 && SESSION_FAIL_DETAILS.has(detail)) {
      localStorage.removeItem("pms_token");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(e);
  }
);

/**
 * Wrap any api request that may need the CEO/L2 elevation header. If the call
 * fails with a 400 mentioning "CEO password", prompts the user and retries
 * with the `X-Approval-Password` header. The user can also pre-supply a
 * password to skip the prompt.
 *
 * Usage:
 *   await withCeoPassword((cfg) => api.put(`/indents/${id}`, payload, cfg))
 */
export async function withCeoPassword(requestFn, presetPassword) {
  try {
    return await requestFn({});
  } catch (err) {
    const detail = err?.response?.data?.detail || "";
    const needsPwd = err?.response?.status === 400 && /CEO password/i.test(detail);
    if (!needsPwd && !presetPassword) throw err;
    const password = presetPassword || window.prompt(`This action requires the CEO password to override.\n\n${detail}`);
    if (!password) throw err;
    return await requestFn({ headers: { "X-Approval-Password": password } });
  }
}

export default api;
